<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import { collapseDrawer, expandDrawer, releaseDrawerHeight } from '@/libs/helpers/drawer-transition';

const props = withDefaults(
  defineProps<{
    /** A container with no detail content shows no chevron and never opens */
    expandable?: boolean;
    /** Small context line under the title, e.g. how long ago the alert was published */
    subtitle?: string;
    /** Warning tag label, e.g. "Déviation" */
    tagLabel?: string;
    title: string;
  }>(),
  {
    expandable: true,
    subtitle: undefined,
    tagLabel: undefined,
  },
);

const open = defineModel<boolean>('open', { default: false });

/**
 * The toggle covers the row, so it must announce what the row says — the tag and
 * the timestamp included, not just the title. The visual text is hidden from
 * assistive tech in turn, or the row would be read twice. The `meta` slot stays
 * out: it holds a link, reachable on its own.
 */
const toggleLabel = computed<string>(() =>
  [props.tagLabel, props.title, props.subtitle].filter(Boolean).join('. '),
);

function toggle() {
  if (props.expandable) open.value = !open.value;
}
</script>

<template>
  <div class="info-container">
    <div class="info-container__header">
      <!--
        The toggle covers the whole row rather than wrapping it: the `meta` slot
        carries a link, and an anchor inside a button is invalid markup no
        browser activates reliably. The slot sits above the overlay instead.
      -->
      <button
        v-if="expandable"
        :aria-expanded="open"
        :aria-label="toggleLabel"
        class="info-container__toggle"
        type="button"
        @click="toggle"
      ></button>
      <span class="info-container__heading">
        <span class="info-container__lead" :aria-hidden="expandable || undefined">
          <span v-if="tagLabel" class="info-container__tag">
            <BaseIcon name="info" class="info-container__tag-icon" />
            {{ tagLabel }}
          </span>
          <span class="info-container__title">{{ title }}</span>
        </span>
        <span v-if="$slots.meta" class="info-container__meta"><slot name="meta" /></span>
        <span v-if="subtitle" class="info-container__subtitle" :aria-hidden="expandable || undefined">
          {{ subtitle }}
        </span>
      </span>
      <span v-if="expandable" class="info-container__chevron" aria-hidden="true">
        <BaseIcon
          name="chevron-down"
          class="info-container__chevron-icon"
          :class="{ 'info-container__chevron-icon--open': open }"
        />
      </span>
    </div>

    <Transition
      name="info-container-body"
      @enter="expandDrawer"
      @afterEnter="releaseDrawerHeight"
      @leave="collapseDrawer"
    >
      <div v-if="expandable && open" class="info-container__body">
        <div class="info-container__panel">
          <div class="info-container__card">
            <slot />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
.info-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__header {
    align-items: center;
    background-color: $color-white;
    border-bottom: 1px solid $color-border-medium;
    display: flex;
    gap: $spacing-4;
    padding: $spacing-4 24px;
    position: relative;
  }

  &__toggle {
    background: none;
    border: none;
    cursor: pointer;
    inset: 0;
    padding: 0;
    position: absolute;
  }

  &__heading {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-3;
    min-width: 0;
  }

  // Lifted out of the toggle overlay so the link it holds stays clickable.
  // `align-self` keeps the lifted area down to the link itself: stretched to the
  // full column, it would swallow the taps meant to open the row.
  &__meta {
    align-self: flex-start;
    max-width: 100%;
    position: relative;
  }

  &__lead {
    display: flex;
    flex-direction: column;
    gap: $spacing-1;
    min-width: 0;
  }

  &__title {
    color: $color-neutral-highest;
    font: $text-bold-medium;
    letter-spacing: $text-bold-medium-spacing;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__subtitle {
    color: $color-neutral-higher;
    font: $text-smaller;
    letter-spacing: $text-smaller-spacing;
  }

  &__tag {
    align-items: center;
    align-self: flex-start;
    background-color: $color-warning-light;
    border-radius: $radius-tag;
    color: $color-warning-dark;
    display: inline-flex;
    font: $label-small;
    gap: 6px;
    letter-spacing: $label-small-spacing;
    padding: 2px 6px;
  }

  &__tag-icon {
    font-size: 16px;
  }

  &__chevron {
    align-items: center;
    border-radius: $radius-pill;
    color: $color-neutral-highest;
    display: flex;
    flex-shrink: 0;
    font-size: 20px;
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  &__chevron-icon {
    transition: transform $duration-drawer-open $easing-drawer;

    &--open {
      transform: rotate(180deg);
    }
  }

  // What animates: no padding of its own, so it collapses to exactly 0.
  &__body {
    overflow: hidden;
  }

  &__panel {
    background-color: $color-neutral-lower;
    padding: 24px;
  }

  &__card {
    background-color: $color-white;
    border: 1px solid $color-border-medium;
    border-radius: $radius-card;
    color: $color-neutral-highest;
    display: flex;
    flex-direction: column;
    font: 400 16px / 24px $font-poppins;
    gap: $spacing-3;
    letter-spacing: $letter-spacing-tight;
    padding: $spacing-4;
  }
}

.info-container-body-enter-active {
  transition:
    height $duration-drawer-open $easing-drawer,
    opacity $duration-drawer-open $easing-drawer;
}

.info-container-body-leave-active {
  transition:
    height $duration-drawer-close $easing-drawer,
    opacity $duration-drawer-close $easing-drawer;
}

.info-container-body-enter-from,
.info-container-body-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .info-container__chevron-icon,
  .info-container-body-enter-active,
  .info-container-body-leave-active {
    transition: none;
  }
}
</style>
